import React from 'react'
import { MiniTopic } from '@deepguide-ai/types'
import { File, Folder, FolderOpen } from 'lucide-react'
import { cn } from '@/lib/utils'
import { IconComponent } from '@/components/icon-component'
import { AVAILABLE_ICONS } from '../emoji-picker/icon-data'

interface DocIconProps {
  topic: MiniTopic
  isExpanded?: boolean
  isSelected?: boolean
  size?: number
  className?: string
}

export function DocIcon({
  topic,
  isExpanded = false,
  isSelected = false,
  size = 16,
  className,
}: DocIconProps) {
  if(!topic) {
    return null
  }
  const hasSubtopics = topic.subtopics && topic.subtopics.length > 0
  const isFolder = topic.type === 'folder' || hasSubtopics

  // Base icon color classes
  const iconColorClass = isSelected
    ? 'text-[var(--primary)]'
    : 'text-muted-foreground'

  // Get computed color for new IconComponent
  const iconColor = isSelected ? 'var(--primary)' : 'var(--muted-foreground)'

  // Check for new icon system first (icon + iconCategory + iconType)
  if (topic.icon) {
    return (
      <div className='relative'>
        <IconComponent
          icon={topic.icon}
          iconCategory={topic.iconCategory || undefined}
          iconType={topic.iconType || undefined}
          iconColor={topic.iconColor || iconColor}
          size={size}
          className={cn('inline', className)}
        />
      </div>
    )
  }

  if (isFolder) {
    // For folders, check if there's a custom pageIcon (legacy)

    if (topic.icon) {
      return (
        <IconComponent
          icon={topic.icon}
          iconCategory={topic.iconCategory || 'LUCIDE'}
          iconType={topic.iconType || 'REGULAR'}
          iconColor={topic.iconColor || iconColor}
          size={size}
          className={cn('inline', className)}
        />
      )
    }

    // Default folder icons
    return (
      <div className='relative'>
        {isExpanded ? (
          <FolderOpen
            size={size}
            strokeWidth={2.2}
            className={cn('opacity-100', iconColorClass, className)}
          />
        ) : (
          <Folder
            size={size}
            strokeWidth={2.2}
            className={cn('opacity-100', iconColorClass, className)}
          />
        )}
      </div>
    )
  } else {
    // For documents, check if there's a custom pageIcon (legacy)
    if (topic.pageIcon) {
      const iconData = AVAILABLE_ICONS.find(
        (iconItem) => iconItem.name === topic.pageIcon
      )
      const LucideIcon = iconData?.icon

      if (LucideIcon) {
        return (
          <LucideIcon
            className={cn('inline', iconColorClass, className)}
            size={size}
            strokeWidth={2.2}
          />
        )
      }
    }

    // Default file icon
    return (
      <File
        size={size}
        strokeWidth={2.2}
        className={cn(iconColorClass, className)}
      />
    )
  }
}
